import { Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { _deleteCompany } from '@/api/deal/mutations';
import AlertTriangleIcon from '@/assets/svg/ui-elements/alert-triangle-outline-orange.svg';
import DeleteIcon from '@/assets/svg/ui-elements/delete.svg';
import { Button, Modal, Separator, Tooltip, Typography } from '@/design-system';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { colors } from '@/shared/design-system/colors';
import { DeniedReasonTrigger, Permission, useCan } from '@/shared/modules/permissions';

export type DeleteDealCompanyModalProps = {
  companyId: string;
  companyName: string;
  offersCount: number;
};

const DELETE_DENIED_TOOLTIP = 'You do not have permission to delete this company (DealDelete).';

export const DeleteDealCompanyModal = () => {
  const { open, handleClose, props } = useGlobalModal('DELETE_DEAL_COMPANY_MODAL');
  const canDeleteCompany = useCan(Permission.DealDelete);
  const { mutate: deleteCompany, isPending } = useMutation({
    mutationFn: _deleteCompany,
    onSuccess: handleClose,
  });

  const handleDelete = () => {
    if (!canDeleteCompany || !props?.companyId) return;
    deleteCompany(props.companyId);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title="Delete Company"
      showClose
      maxWidth={516}
    >
      <Stack flexDirection="column" gap="24px">
        <Separator tone="subtle" />

        <Typography color={colors.text.secondary}>
          You are about to permanently delete{' '}
          <Typography fontWeight={500} as="span">
            {props?.companyName}
          </Typography>
          . This action cannot be undone.
        </Typography>

        {!!props?.offersCount && (
          <Stack
            flexDirection="column"
            gap="12px"
            p="12px"
            borderRadius="12px"
            sx={{ background: 'rgba(255, 255, 255, 0.05)' }}
          >
            <AlertTriangleIcon width={24} height={24} style={{ flexShrink: 0 }} />
            <Stack flexDirection="column" gap="8px">
              <Typography fontWeight={500}>
                This company has {props.offersCount} active offer
                {props.offersCount !== 1 ? 's' : ''}.
              </Typography>
              <Typography variant="body3" color={colors.text.secondary}>
                Deleting this company will also close all associated offers and they will no longer
                be visible to investors.
              </Typography>
            </Stack>
          </Stack>
        )}

        <Stack flexDirection="column" gap="8px">
          <Tooltip title={DELETE_DENIED_TOOLTIP} side="top" disabled={canDeleteCompany}>
            <DeniedReasonTrigger denied={!canDeleteCompany}>
              <Button
                color="destructive"
                onClick={handleDelete}
                disabled={isPending || !canDeleteCompany}
                loading={isPending}
              >
                <DeleteIcon width={16} height={16} />
                Delete Company
              </Button>
            </DeniedReasonTrigger>
          </Tooltip>
          <Button color="secondary" variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
